import React, { useState, useEffect } from 'react';
import {
    Paper,
    Typography,
    Box,
    Grid,
    TextField,
    MenuItem,
    Button,
    Card,
    CardContent,
    Chip,
    LinearProgress,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    FormControlLabel,
    Checkbox,
    Radio,
    RadioGroup,
    Alert,
    InputAdornment,
    FormControl as MuiFormControl,
    InputLabel,
    Select,
    Stack,
    useTheme,
    useMediaQuery,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    AreaChart,
    Area,
} from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth, parseISO, differenceInDays } from 'date-fns';
import {
    Download,
    Refresh,
    Print,
    PictureAsPdf,
    Description,
    FileDownload,
    PhoneAndroid,
    AccountBalance,
    Clear,
    FilterList,
    CalendarToday,
    Edit,
    DateRange,
    Today,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import expenseApi from '../api/expenseApi';
import ExportUtils from '../utils/exportUtils';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];
const SOURCE_COLORS = {
    mpesa: '#4CAF50',
    bank: '#1976d2',
    manual: '#FF9800',
};

const Reports = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(true);
    const [expenses, setExpenses] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportOptions, setExportOptions] = useState({
        format: 'excel',
        includeSummary: true,
        includeCharts: false,
        includeCategoryBreakdown: true,
    });

    // Date range type: 'month', 'year', 'custom'
    const [dateRangeType, setDateRangeType] = useState('month');

    // Enhanced filters
    const [filters, setFilters] = useState({
        year: format(new Date(), 'yyyy'),
        month: format(new Date(), 'M'),
        category: '',
        source: 'all',
        startDate: '',
        endDate: '',
        limit: '',
        fullYear: false,
    });

    const userId = expenseApi.getCurrentUserId();

    useEffect(() => {
        fetchData();
    }, [filters]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                limit: filters.limit ? parseInt(filters.limit) : 10000,
                offset: 0,
            };

            // Handle date range based on type
            if (dateRangeType === 'month') {
                // Month filter
                if (filters.month && filters.year) {
                    params.month = parseInt(filters.month);
                    params.year = parseInt(filters.year);
                }
            } else if (dateRangeType === 'year') {
                // Full year filter
                if (filters.year) {
                    params.start_date = `${filters.year}-01-01`;
                    params.end_date = `${filters.year}-12-31`;
                }
            } else if (dateRangeType === 'custom') {
                // Custom date range
                if (filters.startDate) {
                    params.start_date = filters.startDate;
                }
                if (filters.endDate) {
                    params.end_date = filters.endDate;
                }
            }

            // Add category filter
            if (filters.category) {
                params.category = filters.category;
            }

            // Add source filter
            if (filters.source && filters.source !== 'all') {
                params.source = filters.source;
            }

            const [expensesResponse, statsResponse] = await Promise.all([
                expenseApi.getUserExpenses(userId, params),
                expenseApi.getStatistics(userId),
            ]);

            setExpenses(expensesResponse.data.data.expenses || []);
            setStatistics(statsResponse.data.data);
        } catch (error) {
            console.error('Error fetching report data:', error);
            toast.error('Failed to load report data');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleDateRangeTypeChange = (event, newType) => {
        if (newType !== null) {
            setDateRangeType(newType);
            // Reset date filters when switching
            if (newType === 'month') {
                setFilters(prev => ({
                    ...prev,
                    startDate: '',
                    endDate: '',
                    fullYear: false
                }));
            } else if (newType === 'year') {
                setFilters(prev => ({
                    ...prev,
                    startDate: '',
                    endDate: '',
                    fullYear: true
                }));
            } else if (newType === 'custom') {
                setFilters(prev => ({
                    ...prev,
                    fullYear: false
                }));
            }
        }
    };

    const clearFilters = () => {
        setFilters({
            year: format(new Date(), 'yyyy'),
            month: format(new Date(), 'M'),
            category: '',
            source: 'all',
            startDate: '',
            endDate: '',
            limit: '',
            fullYear: false,
        });
        setDateRangeType('month');
    };

    const handleExportOptionsChange = (event) => {
        const { name, value, checked, type } = event.target;
        setExportOptions(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const data = expenses;
            let filename = `expense_report`;

            if (dateRangeType === 'month') {
                filename = `expense_report_${filters.month}_${filters.year}`;
            } else if (dateRangeType === 'year') {
                filename = `expense_report_${filters.year}_full_year`;
            } else if (dateRangeType === 'custom') {
                filename = `expense_report_${filters.startDate}_to_${filters.endDate}`;
            }

            switch (exportOptions.format) {
                case 'excel':
                    ExportUtils.exportToExcel(data, filename);
                    toast.success('Excel report downloaded successfully');
                    break;
                case 'pdf':
                    let title = 'Expense Report';
                    if (dateRangeType === 'month') {
                        title = `Expense Report - ${months.find(m => m.value === filters.month)?.label} ${filters.year}`;
                    } else if (dateRangeType === 'year') {
                        title = `Expense Report - Full Year ${filters.year}`;
                    } else if (dateRangeType === 'custom') {
                        title = `Expense Report - ${filters.startDate} to ${filters.endDate}`;
                    }
                    ExportUtils.exportToPDF(data, title);
                    toast.success('PDF report downloaded successfully');
                    break;
                case 'csv':
                    ExportUtils.exportToCSV(data, filename);
                    toast.success('CSV report downloaded successfully');
                    break;
                case 'summary':
                    if (statistics) {
                        ExportUtils.exportSummaryToPDF(statistics, data);
                        toast.success('Summary report downloaded successfully');
                    }
                    break;
                default:
                    toast.error('Invalid export format');
            }

            setExportDialogOpen(false);
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export report');
        } finally {
            setExporting(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    // Get source label with icon
    const getSourceLabel = (source) => {
        if (source === 'mpesa') {
            return { label: 'M-Pesa', icon: <PhoneAndroid sx={{ fontSize: 14 }} />, color: '#4CAF50' };
        } else if (source === 'bank') {
            return { label: 'Bank', icon: <AccountBalance sx={{ fontSize: 14 }} />, color: '#1976d2' };
        } else if (source === 'manual') {
            return { label: 'Manual', icon: <Edit sx={{ fontSize: 14 }} />, color: '#FF9800' };
        }
        return { label: 'Unknown', icon: null, color: '#9E9E9E' };
    };

    // Prepare chart data
    const categoryData = expenses.reduce((acc, expense) => {
        const category = expense.category || 'Uncategorized';
        if (!acc[category]) {
            acc[category] = { category, total: 0, count: 0 };
        }
        acc[category].total += parseFloat(expense.amount);
        acc[category].count += 1;
        return acc;
    }, {});

    const pieData = Object.values(categoryData).map(item => ({
        name: item.category,
        value: item.total,
        count: item.count,
    }));

    const dailyData = expenses.reduce((acc, expense) => {
        const date = format(new Date(expense.date), 'dd MMM');
        if (!acc[date]) {
            acc[date] = { date, total: 0, count: 0 };
        }
        acc[date].total += parseFloat(expense.amount);
        acc[date].count += 1;
        return acc;
    }, {});

    const lineData = Object.values(dailyData).sort((a, b) =>
        new Date(a.date) - new Date(b.date)
    );

    // Source distribution for chart
    const sourceData = expenses.reduce((acc, expense) => {
        const source = expense.source || 'manual';
        if (!acc[source]) {
            acc[source] = { source, total: 0, count: 0 };
        }
        acc[source].total += parseFloat(expense.amount);
        acc[source].count += 1;
        return acc;
    }, {});

    const sourceChartData = Object.values(sourceData).map(item => ({
        name: item.source ? item.source.toUpperCase() : 'Manual',
        value: item.total,
        count: item.count,
        color: SOURCE_COLORS[item.source] || '#9E9E9E',
    }));

    const totalExpenses = expenses.reduce((sum, e) => sum + parseFloat(e.amount), 0);

    const years = [];
    for (let i = 2020; i <= new Date().getFullYear(); i++) {
        years.push(i);
    }

    const months = [
        { value: '1', label: 'January' },
        { value: '2', label: 'February' },
        { value: '3', label: 'March' },
        { value: '4', label: 'April' },
        { value: '5', label: 'May' },
        { value: '6', label: 'June' },
        { value: '7', label: 'July' },
        { value: '8', label: 'August' },
        { value: '9', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' },
    ];

    // Get date range display text
    const getDateRangeDisplay = () => {
        if (dateRangeType === 'month') {
            return `${months.find(m => m.value === filters.month)?.label} ${filters.year}`;
        } else if (dateRangeType === 'year') {
            return `Full Year ${filters.year}`;
        } else if (dateRangeType === 'custom') {
            if (filters.startDate && filters.endDate) {
                return `${format(new Date(filters.startDate), 'dd MMM yyyy')} - ${format(new Date(filters.endDate), 'dd MMM yyyy')}`;
            } else if (filters.startDate) {
                return `From ${format(new Date(filters.startDate), 'dd MMM yyyy')}`;
            } else if (filters.endDate) {
                return `Until ${format(new Date(filters.endDate), 'dd MMM yyyy')}`;
            }
            return 'Custom Range';
        }
        return 'All Dates';
    };

    if (loading) {
        return (
            <Box sx={{ width: '100%', mt: 4 }}>
                <LinearProgress />
                <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading report...</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4">Expense Reports</Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                        {getDateRangeDisplay()} • {expenses.length} transactions
                    </Typography>
                </Box>
                <Box>
                    <Tooltip title="Print Report">
                        <IconButton onClick={handlePrint} sx={{ color: '#1976d2' }}>
                            <Print />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Report">
                        <IconButton
                            onClick={() => setExportDialogOpen(true)}
                            sx={{ color: '#1976d2' }}
                        >
                            <FileDownload />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchData} sx={{ color: '#1976d2' }}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                    {/* Date Range Type Toggle */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="text.secondary">
                                Date Range:
                            </Typography>
                            <ToggleButtonGroup
                                value={dateRangeType}
                                exclusive
                                onChange={handleDateRangeTypeChange}
                                size="small"
                                sx={{ flexWrap: 'wrap' }}
                            >
                                <ToggleButton value="month">
                                    <Today sx={{ mr: 0.5, fontSize: 16 }} />
                                    Month
                                </ToggleButton>
                                <ToggleButton value="year">
                                    <DateRange sx={{ mr: 0.5, fontSize: 16 }} />
                                    Full Year
                                </ToggleButton>
                                <ToggleButton value="custom">
                                    <CalendarToday sx={{ mr: 0.5, fontSize: 16 }} />
                                    Custom Range
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Grid>

                    {/* Month/Year Selectors */}
                    {dateRangeType === 'month' && (
                        <>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Year"
                                    name="year"
                                    value={filters.year}
                                    onChange={handleFilterChange}
                                    size="small"
                                >
                                    {years.map((year) => (
                                        <MenuItem key={year} value={year.toString()}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Month"
                                    name="month"
                                    value={filters.month}
                                    onChange={handleFilterChange}
                                    size="small"
                                >
                                    {months.map((month) => (
                                        <MenuItem key={month.value} value={month.value}>
                                            {month.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </>
                    )}

                    {/* Full Year Selector */}
                    {dateRangeType === 'year' && (
                        <Grid item xs={12} sm={3}>
                            <TextField
                                select
                                fullWidth
                                label="Year"
                                name="year"
                                value={filters.year}
                                onChange={handleFilterChange}
                                size="small"
                            >
                                {years.map((year) => (
                                    <MenuItem key={year} value={year.toString()}>
                                        {year} (Full Year)
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    )}

                    {/* Custom Date Range */}
                    {dateRangeType === 'custom' && (
                        <>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    name="startDate"
                                    type="date"
                                    value={filters.startDate}
                                    onChange={handleFilterChange}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    label="End Date"
                                    name="endDate"
                                    type="date"
                                    value={filters.endDate}
                                    onChange={handleFilterChange}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </>
                    )}

                    {/* Source Filter */}
                    <Grid item xs={12} sm={2}>
                        <MuiFormControl fullWidth size="small">
                            <InputLabel>Source</InputLabel>
                            <Select
                                name="source"
                                value={filters.source}
                                onChange={handleFilterChange}
                                label="Source"
                            >
                                <MenuItem value="all">All Sources</MenuItem>
                                <MenuItem value="mpesa">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PhoneAndroid sx={{ fontSize: 18, color: '#4CAF50' }} />
                                        M-Pesa
                                    </Box>
                                </MenuItem>
                                <MenuItem value="bank">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AccountBalance sx={{ fontSize: 18, color: '#1976d2' }} />
                                        Bank
                                    </Box>
                                </MenuItem>
                                <MenuItem value="manual">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Edit sx={{ fontSize: 18, color: '#FF9800' }} />
                                        Manual
                                    </Box>
                                </MenuItem>
                            </Select>
                        </MuiFormControl>
                    </Grid>

                    {/* Category Filter */}
                    <Grid item xs={12} sm={2}>
                        <TextField
                            select
                            fullWidth
                            label="Category"
                            name="category"
                            value={filters.category}
                            onChange={handleFilterChange}
                            size="small"
                        >
                            <MenuItem value="">All Categories</MenuItem>
                            <MenuItem value="Airtime">Airtime</MenuItem>
                            <MenuItem value="PayBill">PayBill</MenuItem>
                            <MenuItem value="Merchant Payment">Merchant Payment</MenuItem>
                            <MenuItem value="Transfer">Transfer</MenuItem>
                            <MenuItem value="Internet & Data">Internet & Data</MenuItem>
                            <MenuItem value="Customer Payment">Customer Payment</MenuItem>
                            <MenuItem value="Service Charges">Service Charges</MenuItem>
                        </TextField>
                    </Grid>

                    {/* Limit */}
                    <Grid item xs={12} sm={2}>
                        <TextField
                            fullWidth
                            label="Limit"
                            name="limit"
                            type="number"
                            value={filters.limit}
                            onChange={handleFilterChange}
                            size="small"
                            placeholder="All"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Leave empty for all transactions">
                                            <Chip size="small" label="All" variant="outlined" />
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    {/* Generate Button */}
                    <Grid item xs={12} sm={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={fetchData}
                            sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' }, height: '100%' }}
                        >
                            Generate Report
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Active Filters Display */}
            {(filters.source !== 'all' || filters.category || filters.limit || dateRangeType !== 'month') && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip
                        label={`Range: ${getDateRangeDisplay()}`}
                        sx={{ bgcolor: '#e3f2fd' }}
                    />
                    {filters.source !== 'all' && (
                        <Chip
                            icon={getSourceLabel(filters.source).icon}
                            label={`Source: ${getSourceLabel(filters.source).label}`}
                            onDelete={() => setFilters(prev => ({ ...prev, source: 'all' }))}
                            sx={{ bgcolor: '#e3f2fd' }}
                        />
                    )}
                    {filters.category && (
                        <Chip
                            label={`Category: ${filters.category}`}
                            onDelete={() => setFilters(prev => ({ ...prev, category: '' }))}
                            sx={{ bgcolor: '#e3f2fd' }}
                        />
                    )}
                    {filters.limit && (
                        <Chip
                            label={`Limit: ${filters.limit} transactions`}
                            onDelete={() => setFilters(prev => ({ ...prev, limit: '' }))}
                            sx={{ bgcolor: '#e3f2fd' }}
                        />
                    )}
                    {(filters.source !== 'all' || filters.category || filters.limit || dateRangeType !== 'month') && (
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<Clear />}
                            sx={{ color: '#d32f2f' }}
                        >
                            Clear All
                        </Button>
                    )}
                </Box>
            )}

            {/* Summary Cards */}
            <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={6} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="caption">
                                Total Expenses
                            </Typography>
                            <Typography variant={isMobile ? 'h6' : 'h5'}>
                                KES {totalExpenses.toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="caption">
                                Transactions
                            </Typography>
                            <Typography variant={isMobile ? 'h6' : 'h5'}>
                                {expenses.length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="caption">
                                Average
                            </Typography>
                            <Typography variant={isMobile ? 'h6' : 'h5'}>
                                KES {expenses.length > 0 ? (totalExpenses / expenses.length).toFixed(0) : 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="caption">
                                Categories
                            </Typography>
                            <Typography variant={isMobile ? 'h6' : 'h5'}>
                                {Object.keys(categoryData).length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={2.4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom variant="caption">
                                Sources
                            </Typography>
                            <Typography variant={isMobile ? 'h6' : 'h5'}>
                                {sourceChartData.length}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                {sourceChartData.map((s) => (
                                    <Chip
                                        key={s.name}
                                        size="small"
                                        label={s.name}
                                        sx={{ fontSize: '0.6rem', height: 20, bgcolor: s.color, color: 'white' }}
                                    />
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, height: 400 }}>
                        <Typography variant="h6" gutterBottom>
                            Daily Spending Trend
                        </Typography>
                        <ResponsiveContainer width="100%" height="90%">
                            <AreaChart data={lineData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <RechartsTooltip
                                    formatter={(value) => `KES ${value.toLocaleString()}`}
                                />
                                <Legend />
                                <Area
                                    type="monotone"
                                    dataKey="total"
                                    stroke="#8884d8"
                                    fill="#8884d8"
                                    fillOpacity={0.3}
                                    name="Daily Spending"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, height: 400 }}>
                        <Typography variant="h6" gutterBottom>
                            Category Distribution
                        </Typography>
                        <ResponsiveContainer width="100%" height="90%">
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <RechartsTooltip
                                    formatter={(value) => `KES ${value.toLocaleString()}`}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Source Distribution Chart */}
            {sourceChartData.length > 0 && (
                <Paper sx={{ p: 3, mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Source Distribution
                    </Typography>
                    <Box sx={{ height: 250 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={sourceChartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <RechartsTooltip
                                    formatter={(value) => `KES ${value.toLocaleString()}`}
                                />
                                <Legend />
                                <Bar dataKey="value" name="Total Amount">
                                    {sourceChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </Paper>
            )}

            {/* Category Breakdown Table */}
            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Category Breakdown
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left', padding: '8px' }}>Category</th>
                                <th style={{ textAlign: 'right', padding: '8px' }}>Transactions</th>
                                <th style={{ textAlign: 'right', padding: '8px' }}>Total Amount</th>
                                <th style={{ textAlign: 'right', padding: '8px' }}>Average</th>
                                <th style={{ textAlign: 'right', padding: '8px' }}>Percentage</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.values(categoryData)
                                .sort((a, b) => b.total - a.total)
                                .map((item) => (
                                    <tr key={item.category}>
                                        <td style={{ padding: '8px' }}>
                                            <Chip label={item.category} size="small" />
                                        </td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>
                                            {item.count}
                                        </td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>
                                            KES {item.total.toLocaleString()}
                                        </td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>
                                            KES {(item.total / item.count).toFixed(0)}
                                        </td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>
                                            {totalExpenses > 0
                                                ? ((item.total / totalExpenses) * 100).toFixed(1)
                                                : 0}%
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </Box>
            </Paper>

            {/* Export Dialog */}
            <Dialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FileDownload sx={{ mr: 1 }} />
                        Export Report
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Export Format
                        </Typography>
                        <FormControl component="fieldset" fullWidth>
                            <RadioGroup
                                name="format"
                                value={exportOptions.format}
                                onChange={handleExportOptionsChange}
                            >
                                <FormControlLabel
                                    value="excel"
                                    control={<Radio />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Description sx={{ mr: 1 }} />
                                            Excel (.xlsx)
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    value="pdf"
                                    control={<Radio />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <PictureAsPdf sx={{ mr: 1 }} />
                                            PDF (.pdf)
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    value="csv"
                                    control={<Radio />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Description sx={{ mr: 1 }} />
                                            CSV (.csv)
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    value="summary"
                                    control={<Radio />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Description sx={{ mr: 1 }} />
                                            Summary PDF
                                        </Box>
                                    }
                                />
                            </RadioGroup>
                        </FormControl>

                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Export Options
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={exportOptions.includeSummary}
                                        onChange={handleExportOptionsChange}
                                        name="includeSummary"
                                    />
                                }
                                label="Include Summary"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={exportOptions.includeCategoryBreakdown}
                                        onChange={handleExportOptionsChange}
                                        name="includeCategoryBreakdown"
                                    />
                                }
                                label="Include Category Breakdown"
                            />
                        </Box>

                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="caption">
                                The report will include {expenses.length} transactions
                                {dateRangeType === 'month' && ` from ${months.find(m => m.value === filters.month)?.label} ${filters.year}`}
                                {dateRangeType === 'year' && ` for full year ${filters.year}`}
                                {dateRangeType === 'custom' && ` from ${filters.startDate} to ${filters.endDate}`}
                                {filters.category && ` for category: ${filters.category}`}
                                {filters.source !== 'all' && ` | Source: ${getSourceLabel(filters.source).label}`}
                                {filters.limit && ` | Limit: ${filters.limit}`}
                            </Typography>
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleExport}
                        variant="contained"
                        disabled={exporting || expenses.length === 0}
                        startIcon={exporting ? <LinearProgress size={20} /> : <FileDownload />}
                        sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
                    >
                        {exporting ? 'Exporting...' : 'Export'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Reports;